/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

/**
 * <size-guide> — clickable text/link that opens the merchant's size chart
 * image or PDF in a modal. Fallback for markets without Sizebay; Sizebay's
 * prescript typically removes or hides this element when it mounts.
 *
 * Uses a vanilla <dialog> (no extra dependencies) instead of porting
 * Fancybox; the dialog is created on first click and re-used after that.
 */
class SizeGuide extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    event.preventDefault();
    const url = this.dataset.url;
    if (!url) return;

    if (!this.#dialog) this.#dialog = this.#buildDialog();
    this.#setContent(url);
    this.#dialog.showModal();
  };

  /** @returns {HTMLDialogElement} */
  #buildDialog() {
    const dialog = document.createElement('dialog');
    dialog.className = 'size-guide-dialog';
    dialog.setAttribute('aria-label', this.getAttribute('alt') || 'Size guide');

    const inner = document.createElement('div');
    inner.className = 'size-guide-dialog__inner';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'size-guide-dialog__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => dialog.close());

    inner.appendChild(closeBtn);
    dialog.appendChild(inner);

    // Click backdrop to close.
    dialog.addEventListener('click', (ev) => {
      if (ev.target === dialog) dialog.close();
    });

    document.body.appendChild(dialog);
    return dialog;
  }

  /** @param {string} url */
  #setContent(url) {
    if (!this.#dialog) return;
    const inner = this.#dialog.querySelector('.size-guide-dialog__inner');
    if (!inner) return;

    // Remove any previous content node, keep the close button.
    inner.querySelector('.size-guide-dialog__content')?.remove();

    const isPdf = /\.pdf(\?|#|$)/i.test(url);
    /** @type {HTMLImageElement | HTMLIFrameElement} */
    let node;
    if (isPdf) {
      node = document.createElement('iframe');
      node.src = url + (url.includes('#') ? '' : '#view=FitH');
      node.title = this.getAttribute('alt') || 'Size guide';
    } else {
      node = document.createElement('img');
      node.src = url;
      node.alt = this.getAttribute('alt') || 'Size guide';
      node.decoding = 'async';
    }
    node.className = 'size-guide-dialog__content';
    inner.appendChild(node);
  }
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuide);
}
